/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * AI scoring on the Leads and Deals lists, kept here so the list pages only
 * add a few lines:
 *
 *   const ai = useAiRecordScores("leads", rowIds);
 *   const [aiSelected, setAiSelected] = useState([]);
 *   columns: [...aiListColumns({ ai, selected: aiSelected, setSelected: setAiSelected, rowIds }), ...]
 *   rows: withAiScores(rows, ai)
 *   <AiBulkScoreButton ai={ai} selected={aiSelected} onDone={...} />
 *
 * The score column appears when the tenant has the AI Assistant; the select
 * column and the button only for users who may score. Selection is capped at
 * BATCH_MAX, the backend's per-run limit.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { Checkbox, CircularProgress, Tooltip } from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { ScoreBadge } from "./AiScoreChip";
import { BATCH_MAX } from "./useAiRecordScores";

export function withAiScores(rows, ai) {
  if (!ai?.enabled) return rows;
  return rows.map((row) => ({ ...row, ai_score: ai.scores[row.id]?.score ?? null }));
}

export function aiListColumns({ ai, selected, setSelected, rowIds }) {
  if (!ai?.enabled) return [];
  const columns = [];
  if (ai.canScore) {
    const chosen = new Set(selected);
    const pageable = rowIds.slice(0, BATCH_MAX);
    const all = pageable.length > 0 && pageable.every((id) => chosen.has(id));
    const some = !all && selected.length > 0;
    const toggle = (id) =>
      setSelected((prev) => {
        if (prev.includes(id)) return prev.filter((x) => x !== id);
        return prev.length >= BATCH_MAX ? prev : [...prev, id];
      });
    columns.push({
      Header: () => (
        <Tooltip title={`Select up to ${BATCH_MAX} to score with AI`}>
          <Checkbox
            size="small"
            checked={all}
            indeterminate={some}
            onChange={() => setSelected(all || some ? [] : pageable)}
            inputProps={{ "aria-label": "Select records to score with AI" }}
            sx={{ p: 0 }}
          />
        </Tooltip>
      ),
      accessor: "ai_select",
      width: "3%",
      disableSortBy: true,
      disableExport: true,
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={chosen.has(row.original.id)}
          onChange={() => toggle(row.original.id)}
          inputProps={{ "aria-label": `Select ${row.original.id} to score with AI` }}
          sx={{ p: 0 }}
        />
      ),
    });
  }
  columns.push({
    Header: "AI Score",
    accessor: "ai_score",
    width: "7%",
    sortType: (a, b) => (a.original.ai_score ?? -1) - (b.original.ai_score ?? -1),
    Cell: ({ row }) => <ScoreBadge score={ai.scores[row.original.id]} />,
  });
  return columns;
}

// Defaults match the other header actions (Template, Bulk Upload, Add Lead):
// dark on the coloured list header. It is disabled until rows are ticked, and
// MUI fades a disabled button to near nothing on that header, so the disabled
// state is kept readable on purpose.
// MUI swaps a disabled contained button's background for its translucent grey,
// which on the list header leaves only floating text. Keep the dark gradient the
// neighbouring Template / Bulk Upload / Add buttons use, just dimmed.
const DISABLED_SX = {
  background: ({ functions: { linearGradient }, palette: { gradients } }) =>
    linearGradient(gradients.dark.main, gradients.dark.state),
  color: "#fff",
  opacity: 0.7,
};

export function AiBulkScoreButton({ ai, selected, onDone, variant = "gradient", color = "dark" }) {
  const [progress, setProgress] = useState(null);
  if (!ai?.canScore) return null;

  const run = async () => {
    setProgress({ done: 0, total: selected.length });
    const summary = await ai.scoreMany(selected, {
      onProgress: (done, total) => setProgress({ done, total }),
    });
    setProgress(null);
    if (onDone) onDone(summary);
  };

  const label = progress
    ? `Scoring ${progress.done}/${progress.total}...`
    : `Score with AI${selected.length ? ` (${selected.length})` : ""}`;
  return (
    <Tooltip title={selected.length ? "" : "Tick the records to score in the list first"}>
      <span>
        <MDButton
          variant={variant}
          color={color}
          onClick={run}
          disabled={!selected.length || ai.scoring}
          startIcon={ai.scoring ? <CircularProgress size={14} color="inherit" /> : <AutoAwesome />}
          sx={{ mr: 2, "&:disabled, &.Mui-disabled": DISABLED_SX }}
        >
          {label}
        </MDButton>
      </span>
    </Tooltip>
  );
}

AiBulkScoreButton.propTypes = {
  ai: PropTypes.shape({
    canScore: PropTypes.bool,
    scoring: PropTypes.bool,
    scoreMany: PropTypes.func,
  }).isRequired,
  selected: PropTypes.arrayOf(PropTypes.number).isRequired,
  onDone: PropTypes.func,
  variant: PropTypes.string,
  color: PropTypes.string,
};

AiBulkScoreButton.defaultProps = { onDone: null, variant: "outlined", color: "info" };

export function describeScoringSummary(summary) {
  const parts = [];
  if (summary.scored) parts.push(`${summary.scored} scored`);
  if (summary.skipped) parts.push(`${summary.skipped} unchanged`);
  if (summary.failed) parts.push(`${summary.failed} could not be scored`);
  if (summary.notFound) parts.push(`${summary.notFound} not available to you`);
  const text = parts.join(", ") || "Nothing was scored";
  return summary.message ? `${text}. ${summary.message}` : `${text}.`;
}
